import { useEffect } from 'react';
import type { FC } from 'react';

import { createAmplitudeSink, createLogRocketTrackSink, registerAnalyticsSinks } from '@modernloop/shared/analytics';
import { useShouldLogStatistics } from '@modernloop/shared/hooks';
import { createDatadogSink, createLogRocketSink, registerObservabilitySinks } from '@modernloop/shared/observability';
import { inIframe, initializeDatadog } from '@modernloop/shared/utils';
import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';

import { useQueryStringParam } from 'src/hooks/useQueryStringParam';

import { DatadogConfig, amplitudeConfig, logrocketConfig, netlifyConfig } from 'src/config';
import { IS_PRODUCTION } from 'src/constants';

// Non Modernloop api urls create a lot of noise in LogRocket
const shouldLogRocketDropUrl = (url: string) => {
  if (url.toLowerCase().includes('api.modernloop.io')) {
    return false;
  }

  if (url.toLowerCase().includes('api.stg.modernloop.io')) {
    return false;
  }

  return true;
};

/**
 * AnalyticsServices
 * - This is a wrapper that bundles loading of analytics services.
 *   It makes loading of our analytics services contingent on accepting of cookies, managed
 *   through OneTrust.
 * - This works in conjunction with the secure-build.js script, which loads OneTrust and hashes the script to enable SRI.
 *   We then check consents from OneTrust to control whether to instantiate our analytics services, and cause cookies to be dropped.
 * - Services impacted:
 *     - Amplitude
 * - Services not impacted:
 *     - LogRocket: We use LogRocket for identifying site errors, so we're considering the cookies necessary
 *     - LaunchDarkly: LD is required for the site to work properly, so its cookies are considered necessary
 * - Note:
 *      When a user changes their cookie settings from "accept" to "deny," we don't remove the cookies that have
 *      already been dropped (this wouldn't stop the tracking calls, necessarily). Instead, we stop making
 *      analytics calls through the service APIs, where possible.
 */

const useDatadogService = () => {
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes browser or analytics APIs after render.
  useEffect(() => {
    initializeDatadog(DatadogConfig);
  }, []);
};

/**
 * Registers the always-on telemetry sinks (Datadog + LogRocket error/message
 * capture, LogRocket product-event tracking) with the vendor-neutral facades.
 * The underlying SDKs no-op until initialized, matching prior behavior.
 */
const useRegisterTelemetrySinks = () => {
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes analytics APIs after render.
  useEffect(() => {
    registerObservabilitySinks([createDatadogSink(), createLogRocketSink()]);
    registerAnalyticsSinks([createLogRocketTrackSink()]);
  }, []);
};

export const AnalyticsServices: FC = () => {
  useDatadogService();
  useRegisterTelemetrySinks();

  const shouldLogStatistics = useShouldLogStatistics();
  const loginWithPopup = useQueryStringParam('loginWithPopup');
  const isInIframe = inIframe();

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes browser or analytics APIs after render.
  useEffect(() => {
    // init LogRocket
    // Only send LogRocket if client side id set
    // We're not recording sessions that happen during loginWithPopup
    // because it creates really short not useful sessions
    if (!loginWithPopup && logrocketConfig.hiringDashboardClientSideID) {
      LogRocket.init(
        isInIframe ? logrocketConfig.candidatePortalClientSideID : logrocketConfig.hiringDashboardClientSideID,
        {
          release: `modernloop@${netlifyConfig.commitRef}`,
          serverURL: logrocketConfig.proxyUrl,
          network: {
            requestSanitizer: (request) => {
              if (shouldLogRocketDropUrl(request.url)) {
                // ignore anything that isn't a modernloop api request
                return null;
              }

              // remove the Authorization header from requests
              if (request.headers) {
                if (request.headers.Authorization) {
                  request.headers.Authorization = '***REDACTED***';
                }
              }

              return request;
            },
          },
          /**
           * If only loading *.modernloop.io in an iframe we want to merge the sessions for candidate portal or never merge sessions for app
           * Logrocket say we need to init logrocket in the iframe for this to work as well as parent window, thats why passing mergeIframes conditionally
           */
          mergeIframes: isInIframe,
        }
      );
      setupLogRocketReact(LogRocket);
    }
  }, [isInIframe, loginWithPopup]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes browser or analytics APIs after render.
  useEffect(() => {
    if (shouldLogStatistics || !IS_PRODUCTION) {
      // The Amplitude sink owns SDK init; registering it here preserves consent gating.
      registerAnalyticsSinks([createAmplitudeSink(amplitudeConfig.apiKey as string)]);
    }
  }, [shouldLogStatistics]);

  console.log('App version', netlifyConfig.commitRef);

  return null;
};
